import React, { useState, useRef, useCallback } from 'react';
import { SupportPageStyle } from './Support.style';
import axios from 'axios';
import { BASE_URL } from '../../constants/api-urls';

interface FormData {
  name: string;
  email: string;
  subject: string;
  category: string;
  message: string;
  priority: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
  attachments?: string;
}

interface FileWithPreview extends File {
  preview?: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_FILES = 5;
const ALLOWED_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'video/mp4',
  'video/webm',
  'video/quicktime',
];

const Support: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    category: 'General Query',
    message: '',
    priority: 'Medium',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [attachments, setAttachments] = useState<FileWithPreview[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // FAQs based on actual Songplace features from User Guide
  const faqs = [
    {
      question: 'How do I schedule songs to be added/removed from playlists automatically?',
      answer:
        'Go to the "Browse" section, search for a song, and click the "+" button. Select your playlist, choose the position, and set start/end dates. The song will be automatically added and removed according to your schedule.',
    },
    {
      question: 'How do I add notes or descriptions to songs?',
      answer:
        'Click the three dots next to any song in your playlist and select "Add note". You can also add notes when scheduling songs. Notes help you remember why you added specific songs to your playlist.',
    },
    {
      question: 'How do I create and manage playlists?',
      answer:
        'Click "Create playlist" in the left menu, enter a name and description, then refresh the page. You can edit playlist details by clicking "Edit playlist" in the playlist header. All playlists sync between Songplace and Spotify.',
    },
    {
      question: 'How do I use Bulk Mode to add multiple songs quickly?',
      answer:
        'In any playlist, enable "Add to bulk" mode, then search for songs and click the "+" button. Each song will be immediately added to your playlist without scheduling options.',
    },
    {
      question: 'How do I link multiple Spotify profiles?',
      answer:
        'Go to "Account overview" (click the three dots next to your name), select "Linked accounts", then click "Add account" to connect additional Spotify profiles. Switch between profiles seamlessly.',
    },
  ];

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Create FormData to support file uploads
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('subject', formData.subject);
      submitData.append('category', formData.category);
      submitData.append('message', formData.message);
      submitData.append('priority', formData.priority);

      // Append attachments
      attachments.forEach(file => {
        submitData.append('attachments', file);
      });

      const response = await axios.post(`${BASE_URL}/api/support/submit`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setSubmitStatus('success');
        setSubmitMessage(
          "Your message has been sent successfully! We'll get back to you within 24-48 hours."
        );
        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          category: 'General Query',
          message: '',
          priority: 'Medium',
        });
        // Clean up file previews and reset attachments
        attachments.forEach(file => {
          if (file.preview) {
            URL.revokeObjectURL(file.preview);
          }
        });
        setAttachments([]);
        setErrors({});
      } else {
        setSubmitStatus('error');
        setSubmitMessage('Failed to send your message. Please try again.');
      }
    } catch (error: unknown) {
      console.error('Support form submission error:', error);
      // Handle specific error messages from backend
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        setSubmitMessage(error.response.data.message);
      } else {
        setSubmitMessage(
          'An error occurred. Please try again later or contact us directly at ali.smak099@gmail.com'
        );
      }
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  // File upload handlers
  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return `${file.name}: Invalid file type. Only images and videos are allowed.`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `${file.name}: File size exceeds 5MB limit.`;
    }
    return null;
  };

  const processFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const validFiles: FileWithPreview[] = [];
      const errorMessages: string[] = [];

      // Check total file count
      if (attachments.length + fileArray.length > MAX_FILES) {
        setErrors(prev => ({
          ...prev,
          attachments: `Maximum ${MAX_FILES} files allowed. You can add ${
            MAX_FILES - attachments.length
          } more.`,
        }));
        return;
      }

      fileArray.forEach(file => {
        const error = validateFile(file);
        if (error) {
          errorMessages.push(error);
        } else {
          // Check for duplicates
          const isDuplicate = attachments.some(
            existing => existing.name === file.name && existing.size === file.size
          );
          if (!isDuplicate) {
            const fileWithPreview = file as FileWithPreview;
            // Create preview for images
            if (file.type.startsWith('image/')) {
              fileWithPreview.preview = URL.createObjectURL(file);
            }
            validFiles.push(fileWithPreview);
          }
        }
      });

      if (errorMessages.length > 0) {
        setErrors(prev => ({
          ...prev,
          attachments: errorMessages.join(' '),
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          attachments: undefined,
        }));
      }

      if (validFiles.length > 0) {
        setAttachments(prev => [...prev, ...validFiles]);
      }
    },
    [attachments]
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        processFiles(files);
      }
    },
    [processFiles]
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
    }
    // Reset input value so the same file can be selected again
    e.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    setAttachments(prev => {
      const newFiles = [...prev];
      // Revoke object URL to prevent memory leaks
      if (newFiles[index].preview) {
        URL.revokeObjectURL(newFiles[index].preview!);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
    // Clear attachment error when removing files
    if (errors.attachments) {
      setErrors(prev => ({
        ...prev,
        attachments: undefined,
      }));
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (type: string): string => {
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎬';
    return '📎';
  };

  return (
    <SupportPageStyle>
      <div className="support-header">
        <div className="headContent">
          <h1 className="support-title">Help & Support</h1>
          <p className="support-subtitle">Get help with your queries or submit a support ticket</p>
        </div>
      </div>

      <div className="support-container">
        <div className="support-content">
          {/* FAQs Section - Left Side */}
          <div className="faqs-section">
            <h2 className="section-title">Frequently asked questions</h2>
            <div className="faqs-list">
              {faqs.map((faq, index) => (
                <div key={index} className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq(index)}>
                    <span>{faq.question}</span>
                    <span className="faq-icon">{expandedFaq === index ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === index && <div className="faq-answer">{faq.answer}</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Contact Form Section - Right Side */}
          <div className="form-section">
            <h2 className="section-title">Contact us</h2>
            <p className="form-description">
              Can't find the answer you're looking for? Submit your query below and we'll get back
              to you.
            </p>

            <form onSubmit={handleSubmit} className="contact-form">
              {/* Name Input */}
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  placeholder="Enter your name"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              {/* Email Input */}
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  placeholder="Enter your email"
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              {/* Subject Input */}
              <div className="form-group">
                <label htmlFor="subject" className="form-label">
                  Subject <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className={`form-input ${errors.subject ? 'error' : ''}`}
                  placeholder="Enter subject"
                />
                {errors.subject && <span className="error-message">{errors.subject}</span>}
              </div>

              {/* Category Dropdown */}
              <div className="form-group">
                <label htmlFor="category" className="form-label">
                  Issue category
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="Technical Issue">Technical Issue</option>
                  <option value="Content Issue">Content Issue</option>
                  <option value="Account Issue">Account Issue</option>
                  <option value="General Query">General Query</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Priority Dropdown */}
              <div className="form-group">
                <label htmlFor="priority" className="form-label">
                  Priority
                </label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>

              {/* Message Textarea */}
              <div className="form-group">
                <label htmlFor="message" className="form-label">
                  Message <span className="required">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className={`form-textarea ${errors.message ? 'error' : ''}`}
                  placeholder="Describe your issue or query in detail..."
                  rows={6}
                />
                {errors.message && <span className="error-message">{errors.message}</span>}
              </div>

              {/* File Upload Section */}
              <div className="form-group">
                <label className="form-label">
                  Attachments <span className="optional">(optional)</span>
                </label>
                <p className="upload-hint">
                  Add screenshots or videos to help explain your issue (max 5 files, 5MB each)
                </p>

                {/* Drag and Drop Zone */}
                <div
                  className={`upload-zone ${isDragging ? 'dragging' : ''} ${
                    attachments.length >= MAX_FILES ? 'disabled' : ''
                  }`}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => attachments.length < MAX_FILES && fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                  />
                  <div className="upload-icon">
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 16V8M12 8L9 11M12 8L15 11"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3 15V16C3 18.2091 4.79086 20 7 20H17C19.2091 20 21 18.2091 21 16V15"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <p className="upload-text">
                    {attachments.length >= MAX_FILES
                      ? 'Maximum files reached'
                      : isDragging
                      ? 'Drop files here...'
                      : 'Drag & drop files here or click to browse'}
                  </p>
                  <span className="upload-formats">Supports: JPG, PNG, GIF, WEBP, MP4, WEBM</span>
                </div>

                {/* File Preview List */}
                {attachments.length > 0 && (
                  <div className="attachments-list">
                    {attachments.map((file, index) => (
                      <div key={`${file.name}-${index}`} className="attachment-item">
                        <div className="attachment-preview">
                          {file.preview ? (
                            <img src={file.preview} alt={file.name} />
                          ) : (
                            <span className="file-icon">{getFileIcon(file.type)}</span>
                          )}
                        </div>
                        <div className="attachment-info">
                          <span className="attachment-name" title={file.name}>
                            {file.name.length > 25 ? file.name.substring(0, 22) + '...' : file.name}
                          </span>
                          <span className="attachment-size">{formatFileSize(file.size)}</span>
                        </div>
                        <button
                          type="button"
                          className="attachment-remove"
                          onClick={() => handleRemoveFile(index)}
                          aria-label="Remove file"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M18 6L6 18M6 6L18 18"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Attachment count indicator */}
                {attachments.length > 0 && (
                  <div className="attachment-count">
                    {attachments.length}/{MAX_FILES} files attached
                  </div>
                )}

                {errors.attachments && <span className="error-message">{errors.attachments}</span>}
              </div>

              {/* Submit Status Messages */}
              {submitStatus === 'success' && (
                <div className="status-message success">{submitMessage}</div>
              )}
              {submitStatus === 'error' && (
                <div className="status-message error">{submitMessage}</div>
              )}

              {/* Submit Button */}
              <button type="submit" className="submit-button" disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="button-content">
                    <span className="loading-spinner"></span>
                    Sending...
                  </span>
                ) : (
                  'Submit'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </SupportPageStyle>
  );
};

export default Support;
